import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { BookOpen, Wallet, TrendingUp, TrendingDown, IndianRupee, Receipt, FileText, PieChart as PieIcon, Plus, Trash2, Calculator, Scale, Building2, ClipboardList, ArrowUpRight, ArrowDownRight, Coins, Landmark, Percent, Settings as SettingsIcon } from "lucide-react";

const FY_OPTIONS = ["FY24-25", "FY25-26", "FY26-27"];
const PIE_COLORS = ["#C85A32","#4A6B53","#8B5CF6","#0EA5E9","#F59E0B","#EC4899","#14B8A6","#EF4444"];

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const inrShort = (n) => {
  const v = Math.abs(Number(n || 0));
  if (v >= 1e7) return "₹" + (n / 1e7).toFixed(2) + " Cr";
  if (v >= 1e5) return "₹" + (n / 1e5).toFixed(2) + " L";
  if (v >= 1e3) return "₹" + (n / 1e3).toFixed(1) + "K";
  return "₹" + Math.round(n || 0);
};

function KpiCard({ label, value, hint, delta, tone = "neutral", testid, icon: Icon }) {
  const toneCol = tone === "success" ? "text-emerald-600" : tone === "danger" ? "text-rose-600" : tone === "primary" ? "text-primary" : "text-foreground";
  return (
    <div className="rounded-2xl bg-card border border-border/60 p-5 hover:border-primary/40 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className={`font-display text-2xl font-semibold ${toneCol}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
      {delta != null && (
        <div className={`text-[11px] mt-1 flex items-center gap-1 ${delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
          {delta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(delta).toFixed(1)}% vs last period
        </div>
      )}
    </div>
  );
}

function DashboardView({ data }) {
  const k = data?.kpis || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        <KpiCard label="Total Receivables" value={inrShort(k.total_receivables)} hint="Open invoices" tone="primary" icon={Receipt} testid="kpi-receivables" />
        <KpiCard label="Total Payables" value={inrShort(k.total_payables)} hint="Vendor & contractor" tone="danger" icon={Wallet} testid="kpi-payables" />
        <KpiCard label="Cash in Bank" value={inrShort(k.cash_in_bank)} hint="All bank accounts" tone="success" icon={Building2} testid="kpi-bank" />
        <KpiCard label="Cash in Hand" value={inrShort(k.cash_in_hand)} hint="Petty cash" icon={Coins} testid="kpi-cash" />
        <KpiCard label="Revenue" value={inrShort(k.revenue)} hint="This FY" tone="success" icon={TrendingUp} testid="kpi-revenue" />
        <KpiCard label="Expenses" value={inrShort(k.expenses)} hint="This FY" tone="danger" icon={TrendingDown} testid="kpi-expenses" />
        <KpiCard label="Net Profit" value={inrShort(k.net_profit)} hint={`Margin ${k.revenue ? ((k.net_profit / k.revenue) * 100).toFixed(1) : 0}%`} tone={k.net_profit >= 0 ? "success" : "danger"} icon={Calculator} testid="kpi-profit" />
        <KpiCard label="Cash Flow (net)" value={inrShort(k.cash_flow_net)} hint="Revenue − Expenses" tone={k.cash_flow_net >= 0 ? "success" : "danger"} icon={IndianRupee} testid="kpi-cashflow" />
        <KpiCard label="Outstanding Invoices" value={k.outstanding_invoices ?? 0} hint="Count · unpaid" icon={FileText} testid="kpi-out-inv" />
        <KpiCard label="Outstanding Bills" value={k.outstanding_bills ?? 0} hint="Vendor bills open" icon={ClipboardList} testid="kpi-out-bills" />
        <KpiCard label="GST Liability" value={inrShort(k.gst_liability)} hint={`Out ${inrShort(k.gst_output)} · In ${inrShort(k.gst_input)}`} tone={k.gst_liability > 0 ? "danger" : "success"} icon={Scale} testid="kpi-gst" />
        <KpiCard label="Project Profit" value={inrShort(k.project_profit_total)} hint="Sum across active projects" tone={k.project_profit_total >= 0 ? "success" : "danger"} icon={Building2} testid="kpi-proj-profit" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cash Flow Chart */}
        <div className="lg:col-span-2 rounded-2xl bg-card border border-border/60 p-5">
          <div className="flex justify-between items-center mb-3">
            <div><h3 className="font-display text-base font-semibold">Cash Flow</h3><p className="text-xs text-muted-foreground">Monthly income vs expense</p></div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data?.cash_flow_series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" />
              <XAxis dataKey="month" fontSize={11} stroke="#73706A" />
              <YAxis fontSize={11} tickFormatter={inrShort} stroke="#73706A" />
              <Tooltip formatter={inr} />
              <Legend />
              <Line type="monotone" dataKey="income" name="Income" stroke="#4A6B53" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="expense" name="Expense" stroke="#C85A32" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="net" name="Net" stroke="#1A1918" strokeDasharray="4 4" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Expense Categories */}
        <div className="rounded-2xl bg-card border border-border/60 p-5">
          <div className="flex justify-between items-center mb-3">
            <div><h3 className="font-display text-base font-semibold">Top Expense Categories</h3><p className="text-xs text-muted-foreground">FY {data?.fy}</p></div>
            <PieIcon className="w-4 h-4 text-muted-foreground" />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data?.expense_by_category || []} dataKey="value" nameKey="name" outerRadius={90} innerRadius={45} paddingAngle={2}>
                {(data?.expense_by_category || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={inr} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-1 mt-2">
            {(data?.expense_by_category || []).slice(0, 6).map((e, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[10px] text-muted-foreground truncate"><span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {e.name}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Project Profitability */}
        <div className="rounded-2xl bg-card border border-border/60 p-5">
          <h3 className="font-display text-base font-semibold mb-3">Project-wise Profit</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.project_profit || []} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" />
              <XAxis type="number" fontSize={11} tickFormatter={inrShort} stroke="#73706A" />
              <YAxis type="category" dataKey="project_name" fontSize={11} width={120} stroke="#73706A" />
              <Tooltip formatter={inr} />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill="#4A6B53" />
              <Bar dataKey="cost" name="Cost" fill="#C85A32" />
              <Bar dataKey="profit" name="Profit" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Receivable Aging */}
        <div className="rounded-2xl bg-card border border-border/60 p-5">
          <h3 className="font-display text-base font-semibold mb-3">Receivable Aging</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.receivable_aging || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" />
              <XAxis dataKey="bucket" fontSize={11} stroke="#73706A" />
              <YAxis fontSize={11} tickFormatter={inrShort} stroke="#73706A" />
              <Tooltip formatter={inr} />
              <Bar dataKey="amount" name="Outstanding" radius={[6, 6, 0, 0]}>
                {(data?.receivable_aging || []).map((r, i) => {
                  const c = r.bucket === "current" ? "#4A6B53" : r.bucket === "90+" ? "#EF4444" : r.bucket === "61-90" ? "#F97316" : "#F59E0B";
                  return <Cell key={i} fill={c} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Journal Entries */}
      <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
        <div className="p-4 border-b border-border/50">
          <h3 className="font-display text-base font-semibold">Recent Journal Entries</h3>
          <p className="text-xs text-muted-foreground">Latest 8 auto-posted + manual entries</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <tr><th className="text-left px-4 py-2">Entry #</th><th className="text-left">Date</th><th className="text-left">Source</th><th className="text-left">Memo</th><th className="text-right px-4">Amount</th></tr>
          </thead>
          <tbody>
            {(data?.recent_entries || []).map((e) => (
              <tr key={e.id} className="border-t border-border/50">
                <td className="px-4 py-2 font-mono text-xs">{e.entry_no}</td>
                <td className="text-xs text-muted-foreground">{e.date}</td>
                <td className="text-xs"><span className="px-2 py-0.5 rounded-full bg-muted/40 text-[10px] uppercase tracking-wide">{e.source}</span></td>
                <td className="text-xs text-muted-foreground truncate max-w-[300px]">{e.memo}</td>
                <td className="text-right font-medium px-4">{inr(e.total_debit)}</td>
              </tr>
            ))}
            {!data?.recent_entries?.length && <tr><td colSpan="5" className="text-center py-10 text-sm text-muted-foreground">No entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChartOfAccountsView() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "asset", parent_code: "", description: "" });
  const load = () => api.get("/accounting/accounts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) return toast.error("Code + name required");
    try { await api.post("/accounting/accounts", form); toast.success("Account added"); setOpen(false); setForm({ code: "", name: "", type: "asset", parent_code: "", description: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this account? Journal-linked accounts cannot be deleted.")) return;
    try { await api.delete(`/accounting/accounts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
  };
  const byType = useMemo(() => {
    const g = {};
    rows.forEach((r) => { (g[r.type] = g[r.type] || []).push(r); });
    return g;
  }, [rows]);
  const typeMeta = { asset: { label: "Assets", color: "text-emerald-700 bg-emerald-50" }, liability: { label: "Liabilities", color: "text-rose-700 bg-rose-50" }, equity: { label: "Equity", color: "text-purple-700 bg-purple-50" }, income: { label: "Income", color: "text-blue-700 bg-blue-50" }, expense: { label: "Expenses", color: "text-amber-700 bg-amber-50" } };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div><h3 className="font-display text-lg">Chart of Accounts</h3><p className="text-xs text-muted-foreground">{rows.length} accounts · double-entry structure</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-account-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" /> New Account</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add Account</DialogTitle><DialogDescription>Extend the chart of accounts with a new ledger.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Code (e.g. 5310)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="account-code-input" />
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(typeMeta).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="col-span-2" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" />
              <Input className="col-span-2" placeholder="Parent code (optional)" value={form.parent_code} onChange={(e) => setForm({ ...form, parent_code: e.target.value })} />
              <Input className="col-span-2" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <Button onClick={save} data-testid="save-account-btn" className="bg-primary text-primary-foreground">Save Account</Button>
          </DialogContent>
        </Dialog>
      </div>
      {Object.entries(typeMeta).map(([t, meta]) => byType[t] && (
        <div key={t} className="rounded-2xl bg-card border border-border/60 overflow-hidden">
          <div className={`p-3 ${meta.color} border-b border-border/50 flex justify-between items-center`}><span className="font-semibold text-sm">{meta.label} ({byType[t].length})</span></div>
          <table className="w-full text-sm">
            <tbody>
              {byType[t].map((a) => (
                <tr key={a.id} className="border-t border-border/50 hover:bg-muted/20">
                  <td className="pl-6 py-2 font-mono text-xs text-muted-foreground w-24">{a.code}</td>
                  <td className="py-2">{a.parent_code && <span className="text-muted-foreground mr-2">└</span>}<span className="font-medium">{a.name}</span></td>
                  <td className="text-right pr-4">
                    <button onClick={() => remove(a.id)} className="p-1.5 hover:bg-muted rounded text-destructive" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function JournalsView() {
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("all");
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), memo: "", lines: [{ account_code: "", debit: 0, credit: 0, memo: "" }, { account_code: "", debit: 0, credit: 0, memo: "" }] });
  const load = () => { const p = source === "all" ? {} : { source }; api.get("/accounting/journals", { params: p }).then((r) => setRows(r.data)); };
  useEffect(() => { load();   }, [source]);
  useEffect(() => { api.get("/accounting/accounts").then((r) => setAccounts(r.data)); }, []);
  const totalDr = form.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCr = form.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0;
  const addLine = () => setForm({ ...form, lines: [...form.lines, { account_code: "", debit: 0, credit: 0, memo: "" }] });
  const rmLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updLine = (i, k, v) => { const lines = [...form.lines]; lines[i] = { ...lines[i], [k]: v }; setForm({ ...form, lines }); };
  const save = async () => {
    if (!balanced) return toast.error("Debits must equal credits and be > 0");
    try { await api.post("/accounting/journals", form); toast.success("Journal posted"); setOpen(false); setForm({ date: new Date().toISOString().slice(0, 10), memo: "", lines: [{ account_code: "", debit: 0, credit: 0, memo: "" }, { account_code: "", debit: 0, credit: 0, memo: "" }] }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this manual journal?")) return; try { await api.delete(`/accounting/journals/${id}`); load(); toast.success("Deleted"); } catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); } };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div><h3 className="font-display text-lg">Journal Entries</h3><p className="text-xs text-muted-foreground">{rows.length} entries · auto-posted + manual</p></div>
        <div className="flex gap-2">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-44" data-testid="journal-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
              <SelectItem value="invoice_payment">Payments</SelectItem>
              <SelectItem value="expense">Expenses</SelectItem>
              <SelectItem value="contract_bill">Contract Bills</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-journal-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" /> New Journal</Button></DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New Manual Journal</DialogTitle><DialogDescription>Debits must equal credits. Every line references an account.</DialogDescription></DialogHeader>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="journal-date" />
                <Input className="col-span-3" placeholder="Memo / narration" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} data-testid="journal-memo" />
              </div>
              <table className="w-full text-sm mb-3">
                <thead className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground"><tr><th className="text-left pb-2">Account</th><th className="text-right pb-2">Debit</th><th className="text-right pb-2">Credit</th><th className="text-left pb-2">Line memo</th><th></th></tr></thead>
                <tbody>
                  {form.lines.map((ln, i) => (
                    <tr key={i}>
                      <td className="pr-1">
                        <Select value={ln.account_code} onValueChange={(v) => updLine(i, "account_code", v)}>
                          <SelectTrigger className="h-9 text-xs" data-testid={`journal-line-account-${i}`}><SelectValue placeholder="Choose account" /></SelectTrigger>
                          <SelectContent>{accounts.filter((a) => a.parent_code).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="pr-1 w-32"><Input type="number" step="0.01" value={ln.debit} onChange={(e) => updLine(i, "debit", e.target.value)} className="text-right h-9 text-xs" data-testid={`journal-line-debit-${i}`} /></td>
                      <td className="pr-1 w-32"><Input type="number" step="0.01" value={ln.credit} onChange={(e) => updLine(i, "credit", e.target.value)} className="text-right h-9 text-xs" data-testid={`journal-line-credit-${i}`} /></td>
                      <td className="pr-1"><Input value={ln.memo} onChange={(e) => updLine(i, "memo", e.target.value)} className="h-9 text-xs" /></td>
                      <td className="w-8"><button onClick={() => rmLine(i)} className="p-1 text-destructive hover:bg-muted rounded" disabled={form.lines.length <= 2}><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-border/50">
                  <tr>
                    <td className="pt-2 text-xs text-muted-foreground"><button onClick={addLine} className="text-primary hover:underline text-xs" data-testid="add-journal-line">+ Add line</button></td>
                    <td className="text-right pt-2 font-medium text-xs">{inr(totalDr)}</td>
                    <td className="text-right pt-2 font-medium text-xs">{inr(totalCr)}</td>
                    <td className={`text-xs pt-2 ${balanced ? "text-emerald-600" : "text-rose-600"}`}>{balanced ? "✓ Balanced" : `⚠ Off by ${inr(Math.abs(totalDr - totalCr))}`}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              <Button onClick={save} disabled={!balanced} className="bg-primary text-primary-foreground w-full" data-testid="save-journal-btn">Post Journal</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((e) => (
          <div key={e.id} className="rounded-xl bg-card border border-border/60 p-4">
            <div className="flex justify-between items-start flex-wrap gap-2 mb-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs">{e.entry_no}</span>
                  <span className="text-xs text-muted-foreground">{e.date}</span>
                  <span className="px-2 py-0.5 rounded-full bg-muted/40 text-[10px] uppercase tracking-wide">{e.source}</span>
                  <span className="text-[10px] text-muted-foreground">{e.fy}</span>
                </div>
                {e.memo && <div className="text-sm mt-1">{e.memo}</div>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold">{inr(e.total_debit)}</span>
                {e.source === "manual" && <button onClick={() => remove(e.id)} className="p-1.5 text-destructive hover:bg-muted rounded"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
            <table className="w-full text-xs mt-2">
              <tbody>
                {(e.lines || []).map((ln) => (
                  <tr key={ln.id} className="border-t border-border/30">
                    <td className="py-1 pl-2 font-mono text-muted-foreground">{ln.account_code}</td>
                    <td className="text-muted-foreground">{ln.account_name}</td>
                    <td className="text-right text-emerald-700">{ln.debit > 0 ? inr(ln.debit) : "—"}</td>
                    <td className="text-right text-rose-700 pr-2">{ln.credit > 0 ? inr(ln.credit) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {!rows.length && <div className="p-10 text-center text-sm text-muted-foreground rounded-2xl border border-dashed border-border/60"><BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />No journal entries. Create an invoice or expense — one will auto-post.</div>}
      </div>
    </div>
  );
}

function ReportsView({ fy }) {
  const [report, setReport] = useState("pnl");
  const [data, setData] = useState(null);
  useEffect(() => {
    const url = { pnl: "/accounting/reports/pnl", bs: "/accounting/reports/balance-sheet", tb: "/accounting/reports/trial-balance", cf: "/accounting/reports/cash-flow" }[report];
    api.get(url, { params: { fy } }).then((r) => setData(r.data));
  }, [report, fy]);
  const rowsToTable = (rows) => (
    <table className="w-full text-sm">
      <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
        <tr><th className="text-left px-4 py-2">Code</th><th className="text-left">Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right px-4">Balance</th></tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.code} className="border-t border-border/50">
            <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
            <td>{r.name}</td>
            <td className="text-right text-emerald-700">{r.debit > 0 ? inr(r.debit) : "—"}</td>
            <td className="text-right text-rose-700">{r.credit > 0 ? inr(r.credit) : "—"}</td>
            <td className="text-right font-semibold px-4">{inr(r.balance)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 border-b border-border/50 pb-2">
        {[["pnl","Profit & Loss"],["bs","Balance Sheet"],["tb","Trial Balance"],["cf","Cash Flow"]].map(([k, label]) => (
          <button key={k} onClick={() => setReport(k)} data-testid={`report-tab-${k}`} className={`px-4 py-2 rounded-lg text-sm border transition-all ${report === k ? "border-primary bg-primary/5 text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{label}</button>
        ))}
      </div>
      {!data && <div className="p-10 text-center text-muted-foreground text-sm">Loading…</div>}
      {data && report === "pnl" && (
        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
          <div className="p-4 border-b border-border/50"><h3 className="font-display text-base font-semibold">Profit & Loss</h3><p className="text-xs text-muted-foreground">{data.from} → {data.to}</p></div>
          <div className="p-4 grid grid-cols-3 gap-4 border-b border-border/50">
            <div><div className="text-[10px] uppercase text-muted-foreground">Revenue</div><div className="font-display text-2xl text-emerald-700">{inr(data.total_income)}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Expenses</div><div className="font-display text-2xl text-rose-700">{inr(data.total_expenses)}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Net Profit ({data.margin_percent}% margin)</div><div className={`font-display text-2xl ${data.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{inr(data.net_profit)}</div></div>
          </div>
          <div className="p-4 space-y-4">
            <div><div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Income</div>{rowsToTable(data.income)}</div>
            <div><div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Expenses</div>{rowsToTable(data.expenses)}</div>
          </div>
        </div>
      )}
      {data && report === "bs" && (
        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
          <div className="p-4 border-b border-border/50"><h3 className="font-display text-base font-semibold">Balance Sheet</h3><p className="text-xs text-muted-foreground">As of {data.as_of}</p></div>
          <div className="grid md:grid-cols-2 gap-0 divide-x divide-border/50">
            <div className="p-4"><div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Assets · {inr(data.total_assets)}</div>{rowsToTable(data.assets)}</div>
            <div className="p-4 space-y-4">
              <div><div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Liabilities · {inr(data.total_liabilities)}</div>{rowsToTable(data.liabilities)}</div>
              <div><div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Equity · {inr(data.total_equity + data.retained_earnings)}</div>{rowsToTable(data.equity)}<div className="mt-2 flex justify-between text-sm border-t border-border/50 pt-2 px-4"><span className="text-muted-foreground">Retained Earnings (P&L to date)</span><span className="font-semibold">{inr(data.retained_earnings)}</span></div></div>
              <div className="flex justify-between text-sm border-t border-border/50 pt-3 px-4 font-semibold">
                <span>Total L + E</span><span>{inr(data.total_liabilities_and_equity)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {data && report === "tb" && (
        <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
          <div className="p-4 border-b border-border/50 flex justify-between items-center">
            <div><h3 className="font-display text-base font-semibold">Trial Balance</h3><p className="text-xs text-muted-foreground">As of {data.as_of}</p></div>
            <span className={`text-xs px-3 py-1 rounded-full ${data.balanced ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{data.balanced ? "✓ Balanced" : "⚠ Unbalanced"}</span>
          </div>
          {rowsToTable(data.rows)}
          <div className="p-4 border-t border-border/50 flex justify-between text-sm font-semibold"><span>Total</span><span>Dr {inr(data.total_debit)} · Cr {inr(data.total_credit)}</span></div>
        </div>
      )}
      {data && report === "cf" && (
        <div className="rounded-2xl bg-card border border-border/60 p-5">
          <h3 className="font-display text-base font-semibold mb-3">Cash Flow · {data.from} → {data.to}</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" />
              <XAxis dataKey="month" fontSize={11} stroke="#73706A" />
              <YAxis fontSize={11} tickFormatter={inrShort} stroke="#73706A" />
              <Tooltip formatter={inr} />
              <Legend />
              <Bar dataKey="income" name="Income" fill="#4A6B53" />
              <Bar dataKey="expense" name="Expense" fill="#C85A32" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function Accounting() {
  const [fy, setFy] = useState("FY26-27");
  const [searchParams, setSearchParams] = useSearchParams();
  const paramTab = searchParams.get("tab");
  const tab = ["dashboard", "coa", "journals", "reports", "banking", "gst", "fy-settings"].includes(paramTab) ? paramTab : "dashboard";
  const setTab = (t) => { const next = new URLSearchParams(searchParams); next.set("tab", t); setSearchParams(next, { replace: true }); };
  const [dash, setDash] = useState(null);
  const loadDash = () => api.get("/accounting/dashboard", { params: { fy } }).then((r) => setDash(r.data));
  useEffect(() => { if (tab === "dashboard") loadDash();   }, [tab, fy]);
  const TABS = [["dashboard", "Dashboard", BookOpen], ["coa", "Chart of Accounts", Scale], ["journals", "Journals", ClipboardList], ["reports", "Reports", FileText]];
  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Finance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold flex items-center gap-3"><Calculator className="w-8 h-8 text-primary" /> Accounting</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time double-entry ledger · auto-posted from every ERP module.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Financial Year</div>
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="w-32" data-testid="fy-select"><SelectValue /></SelectTrigger>
            <SelectContent>{FY_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-2 border-b border-border/50 pb-2 overflow-x-auto">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`acct-tab-${k}`} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition-all ${tab === k ? "border-primary bg-primary/5 text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "dashboard" && <DashboardView data={dash} />}
      {tab === "coa" && <ChartOfAccountsView />}
      {tab === "journals" && <JournalsView />}
      {tab === "reports" && <ReportsView fy={fy} />}
      {tab === "banking" && <div className="rounded-2xl border border-dashed border-border/60 p-16 text-center"><Landmark className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" /><h3 className="font-display text-lg mb-1">Banking Module</h3><p className="text-sm text-muted-foreground max-w-md mx-auto">Bank accounts, deposits, withdrawals, transfers and cheque management ship in Phase 2. Cash-in-bank live totals are already visible on the Dashboard.</p></div>}
      {tab === "gst" && <div className="rounded-2xl border border-dashed border-border/60 p-16 text-center"><Percent className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" /><h3 className="font-display text-lg mb-1">GST & Taxes</h3><p className="text-sm text-muted-foreground max-w-md mx-auto">GSTR-1 / GSTR-3B filing formats and TDS/TCS auto-computation ship in Phase 2. Live GST liability is already visible on the Dashboard (Output − Input).</p></div>}
      {tab === "fy-settings" && <div className="rounded-2xl border border-dashed border-border/60 p-16 text-center"><SettingsIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" /><h3 className="font-display text-lg mb-1">Financial Year Settings</h3><p className="text-sm text-muted-foreground">Use the Financial Year selector in the top-right to switch between FY24-25 / FY25-26 / FY26-27. Multi-company + custom FY calendars ship in Phase 2.</p></div>}
    </div>
  );
}
